import { List as ImmutableList, Map as ImmutableMap, Set as ImmutableSet, Record } from 'immutable';
import uniqBy from 'lodash/uniqBy';
import { DateTime, Interval } from 'luxon';
import { createSelector } from 'reselect';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import {
  constructPredefinedInterval,
  getStartOfMonthFromThisMonth,
  intervalContainsInclusive,
} from 'app-components/utils/date';
import store from 'app-components/utils/redux-store';
import { accountNodeUtils } from '../account-nodes';
import { accountsSelectors, accountsUtils } from '../accounts';
import { accountsBalancesSelectors } from '../accounts-balances';
import { categoriesSelectors } from '../categories';
import { selectorsHelpers } from '../core';
import { goalsSelectors } from '../goals';
import { tagsSelectors } from '../tags';
import { transactionListUtils } from '../transaction-list';
import { transactionsSelectors, transactionsTypes, transactionsUtils } from '../transactions';

import { intervalContainsInclusiveISO } from '../../utils/date/intervalContainsInclusive';
import { nodeAssetIds, nodeDebtIds } from '../account-nodes/accountNodeUtils';
import {
  adjustDateRangeEndToToday,
  capGrouping,
  constructDatePeriods,
  convertAndSortGrouping,
  getGroupingKeys,
  getReportSpecificFilterFromReportName,
  getTimestepFormatting,
  makeGroupByPlural,
} from './reportsUtils';

const { getL2GroupingData } = accountNodeUtils;

// local selector used to filter txns by dateRange (luxon Interval) and a filterObject
export const getReportFilteredTxns = createSelector(
  (state) =>
    accountsSelectors.getLoadPending(state) ||
    categoriesSelectors.getLoadPending(state) ||
    transactionsSelectors.getLoadPending(state),
  selectorsHelpers.createTrimSelector(accountsSelectors.getAccountsById, (account) => ({
    id: account.id,
    type: account.type,
  })),
  (state) =>
    transactionsSelectors.getTransactionsByAccountId(state, {
      txnTypes: transactionsSelectors.txnTypesDefault,
    }),
  (state) => categoriesSelectors.getCategoriesById(state),
  (_, props) => transactionListUtils.createFilterFromFilterObject(props.filterObject),
  (_, props) => props.reportSpecificFilter, // this is for stuff like spending or income, function that accepts txn, true for keep
  (_, props) => props.upcomingTxnsIncluded,
  (_, props) => props.dateRange?.start?.toISODate(),
  (_, props) => props.dateRange?.end?.toISODate(),
  (
    loadPending,
    reportableAccounts,
    txnsById,
    categoriesById,
    filterFn,
    reportSpecificFilter,
    upcomingTxnsIncluded = false,
    dateRangeStartISO,
    dateRangeEndISO,
  ) => {
    if (loadPending) {
      return null;
    }
    const cacheKey = selectorsHelpers.getOrCreateStringFromObject(
      'report-transactions',
      {
        reportableAccounts,
        txnsById,
        filterFn,
        dateRangeEndISO,
        dateRangeStartISO,
        reportSpecificFilter,
        upcomingTxnsIncluded,
      },
      20,
      selectorsHelpers.memoizeWithKeyRetrieveCache, // fn used for garbage collection for unusued object strings
    );
    return selectorsHelpers.memoizeWithKey(
      'report-transactions',
      () => {
        // this is really where the selector starts
        // combine txns from reportable accounts
        const allTxns = ImmutableList().withMutations((boi) => {
          reportableAccounts
            .filter((a) => !accountsUtils.isLoanAccount(a))
            .forEach((acc) => {
              txnsById
                .get(acc.id)
                ?.toList()
                .filter(
                  (txn) =>
                    txn.type !== 'INVESTMENT' || // drop any INVESTMENT non MISCELLANEOUS
                    [
                      transactionsTypes.InvestmentTxnType.INCOME_MISCELLANEOUS,
                      transactionsTypes.InvestmentTxnType.EXPENSE_MISCELLANEOUS,
                    ].includes(txn.investmentTxnType),
                )
                .forEach((txn) => boi.push(txn));
            });
        });

        const withoutUpcomingTxns = !upcomingTxnsIncluded
          ? allTxns.filter((txn) => txn.source !== 'SCHEDULED_TRANSACTION_PENDING')
          : allTxns;

        // filter txns out by their date and reportSpecificFilter
        const dateFilteredReportSpecificTxns =
          dateRangeStartISO || dateRangeEndISO
            ? withoutUpcomingTxns.filter(
                (txn) =>
                  (!reportSpecificFilter || reportSpecificFilter(txn, categoriesById)) &&
                  intervalContainsInclusiveISO(dateRangeStartISO, dateRangeEndISO, txn.postedOn),
              )
            : withoutUpcomingTxns;

        // filter out txns by the filter object if given
        return filterFn ? filterFn(dateFilteredReportSpecificTxns) : dateFilteredReportSpecificTxns;
      },
      cacheKey,
      20,
    );
  },
);
// note - this doesn't account for splits. Not being used at this moment, but will be used once I have implemented splits total filtering - Panelli
export const getFilteredDataOverTime = createSelector(
  (state) =>
    accountsSelectors.getLoadPending(state) ||
    categoriesSelectors.getLoadPending(state) ||
    transactionsSelectors.getLoadPending(state),
  (state, props) =>
    getReportFilteredTxns(state, {
      ...props,
      reportSpecificFilter: getReportSpecificFilterFromReportName(props.reportName),
    }),
  categoriesSelectors.getCategoriesById,
  tagsSelectors.getTags,
  (_, props) => props.dateRange,
  (_, props) => props.filterObject,
  (_, props) => transactionListUtils.createFilterFromFilterObject(props.filterObject),
  () => getReportSpecificFilterFromReportName('spending'),
  (
    loadPending,
    transactions,
    categoriesById,
    tagsById,
    dateRange,
    filterObject,
    filterObjectFn,
    reportSpecificFilter,
  ) => {
    if (loadPending) return null;
    const dateRangeString = JSON.stringify(dateRange);
    const cacheKey = selectorsHelpers.getOrCreateStringFromObject(
      'filtered-data-over-time',
      { transactions, dateRangeString, filterObjectFn },
      5,
      selectorsHelpers.memoizeWithKeyRetrieveCache, // fn used for garbage collection for unusued object strings
    );
    return selectorsHelpers.memoizeWithKey(
      'filtered-data-over-time',
      () => {
        const filterIncludingAppliedFilters = filterObjectFn
          ? (txn, cbid) => reportSpecificFilter(txn, cbid) && filterObjectFn([txn])?.length > 0
          : reportSpecificFilter;
        const overTimeData = getOverTimeData(
          transactions,
          dateRange ?? constructPredefinedInterval({ value: 'LAST_6_MONTHS' }, DateTime.local()),
          'months',
          'categories',
          categoriesById,
          tagsById,
          filterIncludingAppliedFilters,
        );
        const periods = overTimeData?.get('periods');
        let activeMonths = 0;
        let sum = 0;
        periods?.forEach((period) => {
          if (period.total) activeMonths += 1;
          sum += period.total;
        });
        const chartData = periods?.map((period) => {
          const time = DateTime.fromISO(period.timeStamp);
          return {
            month: time.monthShort,
            totalAmount: -1 * period.total,
            totalAmountString: `$${
              -1 * period.total >= 1000 ? ((-1 * period.total) / 1000).toFixed(1) : Math.round(-1 * period.total)
            }${-1 * period.total > 1000 ? 'k' : ''}`,
            currentAmount: -1 * period.total,
          };
        });
        if (activeMonths < 1) activeMonths = 1;
        return { periods: chartData?.toArray(), average: Math.round(sum / activeMonths) };
      },
      cacheKey,
      5,
    );
  },
);

// timeStep is days || months || years
export const getOverTimeData = (
  txns,
  dateRange,
  timeStep,
  groupBy,
  categoriesById,
  tagsById,
  reportSpecificFilter,
  includeAllTxns = false,
) => {
  const periods = constructDatePeriods(dateRange, timeStep).map((period) => ({
    ...period,
    total: 0,
    txns: [],
    txnItems: [],
    ...(groupBy ? { groups: {} } : {}),
  }));

  const allGroups = {};
  const allTxns = [];
  const allTxnItems = [];
  let total = 0;

  const dateStartISO = dateRange?.start?.toISODate();
  const dateEndISO = dateRange?.end?.toISODate();

  txns?.forEach((txn) => {
    const withSplits = txn.split?.items?.size > 0 ? txn.split.items : [txn];
    let insertedTxn = false; // to avoid duplicating txns when split
    const txnDate = txn.postedOn || txn.stDueOn;
    withSplits.forEach((split) => {
      if (
        !intervalContainsInclusiveISO(dateStartISO, dateEndISO, txnDate) ||
        !reportSpecificFilter(txn.merge(split).set('split', undefined), categoriesById)
      ) {
        // purely for checking the split, we merge to check payee and account
        return;
      }
      const period = periods.find(({ isoMask }) => txnDate.startsWith(isoMask)); // obtain period object

      if (!period) {
        return;
      }

      const splitItem = split.id !== txn.id ? split : undefined;
      const txnItem = transactionsTypes.mkTransactionItem({
        transaction: txn,
        splitItem,
        item: {
          ...txn.toObject(),
          ...(Record.isRecord(splitItem) ? splitItem?.toObject() : splitItem),
        },
      });

      if (!txn.isExcludedFromReports || includeAllTxns) {
        // add to total if it's not excluded
        period.total += Number.parseFloat(split.amount);
        total += Number.parseFloat(split.amount);
      }

      if (!groupBy) {
        // no grouping just plain
        if (!insertedTxn) {
          period.txns.push(txn);
          allTxns.push(txn);
          insertedTxn = true;
        }
        period.txnItems.push(txnItem);
        allTxnItems.push(txnItem);
      } else if (groupBy) {
        const { groupKeys } = getGroupingKeys(txn, split, groupBy, categoriesById, tagsById);
        const groupKey = groupKeys[0];
        // const groupKey = getGroupKey(txn, split, groupBy, categoriesById, tagsById);
        if (period.groups[groupKey]) {
          // group has been created
          if (!txn.isExcludedFromReports || includeAllTxns) {
            period.groups[groupKey].total += Number.parseFloat(split.amount);
            period.groups[groupKey].isIgnored = false;
          }
          if (!insertedTxn) {
            period.groups[groupKey].txns.push(txn);
            period.txns.push(txn);
            allTxns.push(txn);
            insertedTxn = true;
          }
          period.groups[groupKey].txnItems.push(txnItem);
          period.txnItems.push(txnItem);
          allTxnItems.push(txnItem);
        } else {
          // group not created yet
          period.groups[groupKey] = {
            isIgnored: txn.isExcludedFromReports && !includeAllTxns,
            txns: [txn],
            txnItems: [txnItem],
            total: !txn.isExcludedFromReports || includeAllTxns ? Number.parseFloat(split.amount) : 0,
          };
          if (!insertedTxn) {
            period.txns.push(txn);
            allTxns.push(txn);
            insertedTxn = true;
          }
          period.txnItems.push(txnItem);
          allTxnItems.push(txnItem);
        }
        allGroups[groupKey] = {
          total:
            (allGroups[groupKey]?.total || 0) +
            (!txn.isExcludedFromReports || includeAllTxns ? Number.parseFloat(split.amount) : 0),
          // If any period has a group key that is not ignored, then the allGroups
          // copy of that group key should also not be ignored. Only if the group
          // is ignored from all periods should we set this to true. If even one
          // period has isIgnored set to false, then we leave the all groups isIgnored flag false.
          isIgnored: allGroups[groupKey]?.isIgnored === false ? false : period.groups[groupKey].isIgnored,
        };
      }
    });
  });

  return ImmutableMap({
    total,
    allTxns: ImmutableList(allTxns),
    allTxnItems: ImmutableList(allTxnItems),
    periods,
    allGroups,
  });
};

// if getting summed data must have a groupBy that's not By Total
const getTotalData = (
  txns,
  dateRange,
  timeStep,
  groupBy,
  categoriesById,
  tagsById,
  reportSpecificFilter,
  includeAllTxns = false,
) => {
  // const start = performance.now();
  const groups = {};
  let allTotal = 0;

  const allTxnItems = [];
  txns.forEach((txn) => {
    const withSplits = txn.split?.items?.size > 0 ? txn.split.items : [txn];

    // we have to keep track of which groups the txn has been added to to avoid duplucating in the same group/subgroup but not different ones, bleh
    const groupsWithTxn = [];

    withSplits.forEach((split) => {
      if (!reportSpecificFilter(txn.merge(split).set('split', undefined), categoriesById)) {
        // purely for checking the split, we merge to check the payee and account
        return;
      }

      const splitItem = split.id !== txn.id ? split : undefined;
      const txnItem = transactionsTypes.mkTransactionItem({
        transaction: txn,
        splitItem,
        item: {
          ...txn.toObject(),
          ...(Record.isRecord(splitItem) ? splitItem?.toObject() : splitItem),
        },
      });
      allTxnItems.push(txnItem);

      // const groupKey = getGroupKey(txn, split, groupBy, categoriesById, tagsById);
      const { groupKeys, subKey, parentCategoryId, subCategoryId } = getGroupingKeys(
        txn,
        split,
        groupBy,
        categoriesById,
        tagsById,
      );
      let alreadyInAllTotal = false;
      groupKeys.forEach((groupKey) => {
        if (groups[groupKey]) {
          // group exists
          if (!groupsWithTxn.includes(groupKey)) {
            groupsWithTxn.push(groupKey);
            groups[groupKey].txns.push(txn);
          }
          groups[groupKey].txnItems.push(txnItem);

          if (!txn.isExcludedFromReports || includeAllTxns) {
            groups[groupKey].total += Number.parseFloat(split.amount);
            allTotal += alreadyInAllTotal ? 0 : Number.parseFloat(split.amount);
            alreadyInAllTotal = true;
            groups[groupKey].isIgnored = false;
          }

          if (subKey) {
            // if it has a subkey then put it in its subgroup
            if (!groups[groupKey].subgroups[subKey]) {
              // if the subgroup hasn't been created yet
              groups[groupKey].subgroups[subKey] = {
                total: 0,
                isIgnored: txn.isExcludedFromReports && !includeAllTxns,
                txns: [],
                txnItems: [],
                groupKey: subKey,
                categoryId: subCategoryId,
              };
            }

            if (!txn.isExcludedFromReports || includeAllTxns) {
              groups[groupKey].subgroups[subKey].isIgnored = false;
            }
            groups[groupKey].subgroups[subKey].total +=
              !txn.isExcludedFromReports || includeAllTxns ? Number.parseFloat(split.amount) : 0;

            if (!groupsWithTxn.includes(groupKey + subKey)) {
              groupsWithTxn.push(groupKey + subKey);
              groups[groupKey].subgroups[subKey].txns.push(txn);
            }
            groups[groupKey].subgroups[subKey].txnItems.push(txnItem);
          }
        } else {
          // group does not exist
          groups[groupKey] = {
            groupKey,
            isIgnored: txn.isExcludedFromReports && !includeAllTxns,
            categoryId: parentCategoryId,
            txns: [txn],
            txnItems: [txnItem],
            total: !txn.isExcludedFromReports || includeAllTxns ? Number.parseFloat(split.amount) : 0,
            subgroups: subKey
              ? {
                  [subKey]: {
                    total: !txn.isExcludedFromReports || includeAllTxns ? Number.parseFloat(split.amount) : 0,
                    isIgnored: txn.isExcludedFromReports && !includeAllTxns,
                    txns: groupsWithTxn.includes(groupKey + subKey) ? [] : [txn],
                    txnItems: [txnItem],
                    groupKey: subKey,
                    categoryId: subCategoryId,
                  },
                }
              : {},
          };
          groupsWithTxn.push(groupKey + (subKey || ''));
          if (!alreadyInAllTotal) {
            allTotal += !txn.isExcludedFromReports || includeAllTxns ? Number.parseFloat(split.amount) : 0;
            alreadyInAllTotal = true;
          }
        }
      });
    });
  });

  return ImmutableMap({
    caller: 'getTotalData',
    allTxns: ImmutableList(txns),
    allTxnItems: ImmutableList(allTxnItems),
    groups,
    total: allTotal,
  });
};

const filterAccounts = (accountsById, reportableAccounts, filterObject) =>
  accountsById?.filter(
    (
      acc,
      accountId, // at this time the only filter allowed for Savings and NetWorth report are accounts
    ) =>
      reportableAccounts.has(accountId) && // account is reportable
      // only one of the following lines has to be true for the accountId to be kept
      (!filterObject?.get('accounts') ||
        filterObject.get('accounts').isEmpty() || // there's no filter or no filters for accounts, or the filter for accounts is empty
        (!filterObject?.get('filterOut') && filterObject?.get('accounts')?.includes(accountId)) || // filtering in and it's included in accounts
        (filterObject?.get('filterOut') && !filterObject?.get('accounts')?.includes(accountId))),
  ); // filtering out and it's not included in accounts'

export const getAccountBalances = createSelector(
  (state) =>
    accountsSelectors.getLoadPending(state) ||
    categoriesSelectors.getLoadPending(state) ||
    transactionsSelectors.getLoadPending(state) ||
    tagsSelectors.getLoadPending(state),
  (state) => {
    const accounts = accountsSelectors.getAccountsById(state);
    const excludeBalance = featureFlagsSelectors.getFeatureFlag(state, 'excludeAccountBalanceFromReports');
    if (excludeBalance) {
      return accounts.filter((account) => !account.isExcludedFromReports);
    }
    return accounts;
  },
  (state) => transactionsSelectors.getTransactionsByAccountId(state),
  (_, props) => props.accountsById,
  (_, props) => props.dateRange.toISO(),
  (_, props) => props.timeStep,
  (_, props) => props.filterObject,
  (_, props) => getReportSpecificFilterFromReportName(props.reportName),
  (state) => accountsBalancesSelectors.getSimplifiedBalancesByAccount(state, accountsSelectors.getAccountsById(state)),
  (
    loadPending,
    reportableAccounts,
    txnsByAccountId,
    accountsById,
    dateRangeISO,
    timeStep,
    filterObject,
    reportSpecificFilter,
    investmentAndLoanBalances,
  ) => {
    if (loadPending) {
      return null;
    }
    const dateRange = Interval.fromISO(dateRangeISO);
    const accountsToCalculate = filterAccounts(accountsById, reportableAccounts, filterObject);

    const balancePeriods = constructDatePeriods(dateRange, timeStep);
    const periodsByAcctId = constructDatePeriods(dateRange, timeStep);
    let allTxns = ImmutableList();
    let allTxnItems = ImmutableList();
    const retTxnsByAccountId = {};
    const txnItemsByAccountId = {};

    const timeStepWithoutS = getTimestepFormatting(timeStep);

    accountsToCalculate.forEach((acc, accountId) => {
      // First, we get all the txns and retTxnsByAcctId
      const normalizedName = acc.name.replaceAll(/\(|\)/gm, ' '); // SIMPL-22712
      const txns = txnsByAccountId.get(accountId).toList();
      const filterIncludingAppliedFilter = filterObject
        ? (txn, cbid) =>
            reportSpecificFilter(txn, cbid) && transactionsUtils.createTransactionsFilterByObjFn(filterObject)(txn)
        : reportSpecificFilter;
      // we calculate the data up until today
      const overTimeData = getOverTimeData(
        txns,
        adjustDateRangeEndToToday(dateRange),
        timeStep,
        null,
        null,
        null,
        filterIncludingAppliedFilter,
        true,
      );
      // but then we remove the txns outside the range
      const dateCorrectedTxns = overTimeData
        .get('allTxns')
        .filter((txn) => intervalContainsInclusive(dateRange, DateTime.fromISO(txn.postedOn)));
      allTxns = allTxns.concat(dateCorrectedTxns);
      retTxnsByAccountId[normalizedName] = dateCorrectedTxns;

      const transactionIds = ImmutableSet(dateCorrectedTxns.map((txn) => txn.id));
      const dateCorrectedTxnItems = transactionsSelectors
        .getTransactionItemsByTransactionIds(store.getState(), transactionIds)
        .toList();
      allTxnItems = allTxnItems.concat(dateCorrectedTxnItems);
      txnItemsByAccountId[normalizedName] = dateCorrectedTxnItems;

      // We grab the total balances of the accounts sorted by day
      const balancesByDay = investmentAndLoanBalances.find((myAcc) => myAcc.id === accountId)?.accountBalances;

      // Get the periods we will cycle through
      const periods = constructDatePeriods(dateRange, timeStep);

      let periodIndex = balancePeriods.size - 1;
      periods.reverse().forEach((period) => {
        if (intervalContainsInclusive(dateRange, DateTime.fromISO(period.timeStamp).startOf('day'))) {
          // Sanity test

          // The date we will use for the requested timestep
          let periodTimeStamp = DateTime.fromISO(period.timeStamp).toISODate();

          //  If it is the last period we are going through, set the date to the END of the timestep so we get the FINAL VALUE of the month
          //  This is ONLY used for balances values because in a monthly timestep, we want the value at the END of the month, not the middle or beginning
          if (periodIndex === 0) {
            periodTimeStamp = DateTime.fromISO(period.timeStamp).endOf(timeStepWithoutS).toISODate();
          }

          const total = balancesByDay?.find((day) => day.balanceOn === periodTimeStamp)?.balanceAmount;
          if (typeof total === 'number') {
            if (
              typeof periodsByAcctId.get(periodIndex).total !== 'number' ||
              typeof balancePeriods.get(periodIndex).total !== 'number'
            ) {
              periodsByAcctId.get(periodIndex).total = 0;
              balancePeriods.get(periodIndex).total = 0;
            }

            periodsByAcctId.get(periodIndex)[acc.id] = total;
            periodsByAcctId.get(periodIndex).total += total;
            balancePeriods.get(periodIndex)[normalizedName] = total;
            balancePeriods.get(periodIndex).total += total;
          } else {
            periodsByAcctId.get(periodIndex)[acc.id] = null;
            balancePeriods.get(periodIndex)[normalizedName] = null;
          }
          periodIndex -= 1;
        }
      });
    });
    return ImmutableMap({
      periodsByAcctId,
      periods: balancePeriods,
      txnsByAccountId: retTxnsByAccountId,
      txnItemsByAccountId,
      allTxns,
      allTxnItems,
    });
  },
);

// ================================================================================================================================== //
//                                                      For Display Selectors                                                         //
// ================================================================================================================================== //

export const getIncomeSpendingReportForDisplay = createSelector(
  (state) =>
    accountsSelectors.getLoadPending(state) ||
    categoriesSelectors.getLoadPending(state) ||
    transactionsSelectors.getLoadPending(state) ||
    tagsSelectors.getLoadPending(state),
  (state, props) =>
    getReportFilteredTxns(state, {
      ...props,
      reportSpecificFilter: getReportSpecificFilterFromReportName(props.reportName),
    }),
  (state) => categoriesSelectors.getCategoriesById(state),
  (state) => tagsSelectors.getTags(state),
  (_, props) => props.reportName,
  (_, props) => props.dateRange,
  (_, props) => props.timeStep,
  (_, props) => props.isOverTime,
  (_, props) => props.chartType,
  (_, props) => props.groupBy,
  (_, props) => transactionListUtils.createFilterFromFilterObject(props.filterObject),
  (_, props) => getReportSpecificFilterFromReportName(props.reportName),
  (
    loadPending,
    txnsList,
    categoriesById,
    tagsById,
    reportName,
    dateRange,
    timeStep,
    isOverTime,
    chartType,
    groupBy,
    filterObjectFn,
    reportSpecificFilter,
  ) => {
    if (loadPending || !txnsList) {
      return null;
    }
    const cacheKey = selectorsHelpers.getOrCreateStringFromObject(
      'reports',
      {
        txnsList,
        reportName,
        date: dateRange.toISO(),
        timeStep,
        isOverTime,
        chartType,
        groupBy,
        reportSpecificFilter,
        categoriesById,
        tagsById,
        filterObjectFn,
      },
      6,
      selectorsHelpers.memoizeWithKeyRetrieveCache,
    );

    return selectorsHelpers.memoizeWithKey(
      'reports',
      () => {
        const retData = { allTxns: null, chartData: {} };
        const filterIncludingAppliedFilters = filterObjectFn
          ? (txn, cbid) => reportSpecificFilter(txn, cbid) && filterObjectFn([txn])?.length > 0
          : reportSpecificFilter;
        const report = (isOverTime ? getOverTimeData : getTotalData)(
          txnsList,
          dateRange,
          timeStep,
          groupBy,
          categoriesById,
          tagsById,
          filterIncludingAppliedFilters,
        );
        if (!report) return null;
        retData.allTxns = report.get('allTxns');
        retData.allTxnItems = report.get('allTxnItems');
        if (chartType === 'bar' || chartType === 'stacked bar' || chartType === 'grouped bar') {
          const signMutliple = reportName === 'spending' ? -1 : 1;

          retData.chartData.orderedFields = Object.entries(report.get('allGroups') || {})
            .map(([label, value]) => ({ label, total: value.total, isIgnored: value.isIgnored }))
            .sort((a, b) => signMutliple * b.total - signMutliple * a.total)
            .filter(({ isIgnored }) => !isIgnored)
            .map(({ label }) => label);

          if (retData.chartData.orderedFields.length > 4) {
            retData.chartData.orderedFields = retData.chartData.orderedFields.slice(0, 4).concat(['Everything else']);
          }

          const allZeroGroups = {}; // we need to zero out all the groups for each period for "consistency"
          retData.chartData.orderedFields.forEach((groupKey) => {
            allZeroGroups[groupKey] = 0;
          });
          retData.total = report.get('total');

          retData.chartData.periods =
            report
              ?.get('periods')
              ?.map((period) => {
                const groupObject = period.groups
                  ? { txns: period.txns, txnItems: period.txnItems }
                  : { total: period.total, txns: period.txns, txnItems: period.txnItems };
                if (period.groups) {
                  // handle groups of data
                  Object.entries(period.groups).forEach(([groupKey, groupData]) => {
                    const cappedKey = retData.chartData.orderedFields.includes(groupKey) ? groupKey : 'Everything else';
                    groupObject[cappedKey] = (groupObject[cappedKey] || 0) + signMutliple * groupData.total;
                  });
                }

                return {
                  timeStamp: period.timeStamp,
                  ...allZeroGroups,
                  ...groupObject,
                };
              })
              .toArray() || null;
        } else if (chartType === 'sideways bar' || chartType === 'pie') {
          const convertedGroups = convertAndSortGrouping(
            report?.get('groups') || {},
            reportName === 'spending' ? -1 : 1,
          );
          const [cappedGroups, allTxns, allTotal, allTxnItems] = capGrouping(
            convertedGroups,
            9,
            'Everything else',
            true,
          );

          retData.chartData.totals = [
            {
              groupKey: `All ${makeGroupByPlural(groupBy)}`,
              subgroups: cappedGroups,
              total: allTotal,
              txns: allTxns,
              txnItems: allTxnItems,
            },
          ];
          retData.chartData.orderedFields = cappedGroups.map((g) => g.groupKey);
          retData.total = report.get('total');
        }
        return ImmutableMap({ ...retData, rawReport: report });
      },
      cacheKey,
      6,
    );
  },
);

export const getNetIncomeReportForDisplay = createSelector(
  (state) =>
    accountsSelectors.getLoadPending(state) ||
    categoriesSelectors.getLoadPending(state) ||
    transactionsSelectors.getLoadPending(state) ||
    tagsSelectors.getLoadPending(state),
  (state, props) =>
    getReportFilteredTxns(state, { ...props, reportSpecificFilter: getReportSpecificFilterFromReportName('spending') }),
  (state, props) =>
    getReportFilteredTxns(state, { ...props, reportSpecificFilter: getReportSpecificFilterFromReportName('income') }),
  (state) => categoriesSelectors.getCategoriesById(state),
  (state) => tagsSelectors.getTags(state),
  (_, props) => props.reportName,
  (_, props) => props.dateRange,
  (_, props) => props.timeStep,
  (_, props) => props.isOverTime,
  (_, props) => props.chartType,
  (_, props) => props.groupBy,
  (_, props) => transactionListUtils.createFilterFromFilterObject(props.filterObject),
  (
    loadPending,
    spendingTxns,
    incomeTxns,
    categoriesById,
    tagsById,
    reportName,
    dateRange,
    timeStep,
    isOverTime,
    chartType,
    groupBy,
    filterObjectFn,
  ) => {
    if (loadPending) {
      return null;
    }

    const cacheKey = selectorsHelpers.getOrCreateStringFromObject(
      'reports',
      {
        spendingTxns,
        incomeTxns,
        date: dateRange.toISO(),
        timeStep,
        isOverTime,
        groupBy,
        categoriesById,
        tagsById,
        filterObjectFn,
      },
      6,
      selectorsHelpers.memoizeWithKeyRetrieveCache,
    );

    return selectorsHelpers.memoizeWithKey(
      'reports',
      () => {
        const spendingFilter = getReportSpecificFilterFromReportName('spending');
        const spendingFilterIncludingAppliedFilters = filterObjectFn
          ? (txn, cbid) => spendingFilter(txn, cbid) && filterObjectFn([txn])?.length > 0
          : spendingFilter;
        const spendingReport = (isOverTime ? getOverTimeData : getTotalData)(
          spendingTxns,
          dateRange,
          timeStep,
          groupBy,
          categoriesById,
          tagsById,
          spendingFilterIncludingAppliedFilters,
        );

        const incomeFilter = getReportSpecificFilterFromReportName('income');
        const incomeFilterIncludingAppliedFilters = filterObjectFn
          ? (txn, cbid) => incomeFilter(txn, cbid) && filterObjectFn([txn])?.length > 0
          : incomeFilter;
        const incomeReport = (isOverTime ? getOverTimeData : getTotalData)(
          incomeTxns,
          dateRange,
          timeStep,
          groupBy,
          categoriesById,
          tagsById,
          incomeFilterIncludingAppliedFilters,
        );

        const retData = { allTxns: null, allTxnItems: null, chartData: {} };
        retData.allTxns = ImmutableList(
          uniqBy(spendingReport.get('allTxns').concat(incomeReport.get('allTxns')).toArray(), (txn) => txn.id),
        );
        retData.allTxnItems = ImmutableList(
          spendingReport.get('allTxnItems').concat(incomeReport.get('allTxnItems')).toArray(),
        );
        retData.chartData.periods =
          spendingReport
            .get('periods')
            ?.toArray()
            .map((period, index) => {
              const spendingTotal = period.total;
              const incomeTotal = incomeReport.get('periods').get(index).total;
              const combinedTxns = period.txns.concat(incomeReport.get('periods').get(index).txns);
              const uniqueCombinedTxns = uniqBy(combinedTxns, (e) => e.id); // eliminate duplicate txns
              const combinedTxnItems = period.txnItems.concat(incomeReport.get('periods').get(index).txnItems);
              return {
                txns: uniqueCombinedTxns || [],
                txnItems: combinedTxnItems || [],
                spendingTotal: -spendingTotal,
                incomeTotal,
                netIncomeTotal: incomeTotal + spendingTotal,
                timeStamp: period.timeStamp,
              };
            }) || [];
        retData.total = incomeReport.get('total') + spendingReport.get('total');
        return ImmutableMap({ ...retData, rawReport: spendingReport });
      },
      cacheKey,
      6,
    );
  },
);

export const getSavingsReportForDisplay = createSelector(
  (state) => accountsSelectors.getLoadPending(state) || transactionsSelectors.getLoadPending(state),
  (state, props) =>
    getAccountBalances(state, {
      ...props,
      accountsById: accountsSelectors.getSavingAccounts(state),
    }),
  (_, props) => props.chartType,
  (_, props) => props.groupBy,
  (loadPending, report, chartType, groupBy) => {
    if (loadPending || !report) {
      return null;
    }

    const retData = { allTxns: null, allTxnItems: null, chartData: {} };

    retData.chartData.orderedFields =
      !groupBy || groupBy === 'none'
        ? ['total']
        : Object.entries(report.get('periods').last()) // destructure to entries list
            .filter(
              ([accountName, _value]) =>
                accountName !== 'total' && accountName !== 'timeStamp' && accountName !== 'isoMask',
            ) // remove the non-account properties
            .sort(([_aN1, value1], [_aN2, value2]) => value2 - value1) // sort based on final month value
            .map(([accountName, _value]) => accountName); // reduce to just account names
    retData.total = report.get('periods').last()?.total;
    retData.allTxns = report.get('allTxns');
    retData.allTxnItems = report.get('allTxnItems');

    if (chartType === 'sideways bar' || chartType === 'pie') {
      const finalMonth = report.get('periods').last();
      const groups = retData.chartData.orderedFields.map((accountName) => ({
        groupKey: accountName,
        subgroups: null,
        total: finalMonth[accountName],
        txns: groupBy === 'none' ? report.get('allTxns') : report.get('txnsByAccountId')[accountName],
        txnItems: groupBy === 'none' ? report.get('allTxnItems') : report.get('txnItemsByAccountId')[accountName],
      }));

      const convertedGroups = convertAndSortGrouping(groups || {}, 1);
      const [cappedGroups, allTxns, allTotal, allTxnItems] = capGrouping(convertedGroups, 9, 'Everything else');

      retData.chartData.totals = [
        { groupKey: 'All Savings', subgroups: cappedGroups, total: allTotal, txns: allTxns, txnItems: allTxnItems },
      ];
    } else {
      retData.chartData.periods = report.get('periods').toArray();
    }

    return ImmutableMap({ ...retData, rawReport: report });
  },
);

const getAccountsInPeriods = (periodsByAcctId, accountsById) =>
  accountsById.filter((acc) => periodsByAcctId.some((date) => typeof date[acc.id] === 'number'));

const getNetWorthTableData = (periods, accountsById, timeStep, filterObject, reportableAccounts) => {
  let formatString = 'MMM yyyy';
  if (timeStep === 'days') {
    formatString = 'MMM dd';
  } else if (timeStep === 'years') {
    formatString = 'yyyy';
  }

  const dateList = periods.map((date) => DateTime.fromISO(date.timeStamp).toFormat(formatString));

  const filteredAccounts = filterAccounts(accountsById, reportableAccounts, filterObject);

  const reformattedAccounts = filteredAccounts.map((account) => {
    const selectedData = {};
    periods.forEach((day) => {
      const amount = day[account.id];
      const formattedDate = DateTime.fromISO(day.timeStamp).toFormat(formatString);
      selectedData[formattedDate] = amount;
    });

    if (Object.keys(selectedData).length !== 0) {
      return {
        accountName: account?.name,
        data: selectedData,
        type: getL2GroupingData(null, account),
      };
    }
    return null;
  });

  let numberOfDebtTypes = 0;
  let numberOfAssetTypes = 0;

  const zeroValueDates = {};

  dateList.forEach((date) => {
    zeroValueDates[date] = null;
  });

  const assets = {
    displayLabel: 'Assets',
    headerData: { ...zeroValueDates },
    subData: {},
  };

  const debt = {
    displayLabel: 'Debt',
    headerData: { ...zeroValueDates },
    subData: {},
  };

  const netWorth = {
    headerData: { ...zeroValueDates },
    displayLabel: 'Net Worth',
  };

  reformattedAccounts.forEach((account) => {
    if (account) {
      const typeId = account.type.id;
      const { displayLabel } = account.type;
      // debts go first because 'OTHER' (catchall) exists in assets and can match OTHER_LIABILITY
      if (nodeDebtIds.some((id) => typeId.includes(id))) {
        if (typeId && displayLabel && !debt.subData[typeId]) {
          numberOfDebtTypes += 1;
          debt.subData[typeId] = {
            displayLabel,
            accounts: [],
            headerData: { ...zeroValueDates },
          };
        }
        dateList.forEach((date) => {
          if (typeof account.data[date] === 'number') {
            // We now know the acct isn't null, by default it is null, so we must change it to 0 so it can be summed
            if (typeof debt.headerData[date] !== 'number') {
              debt.headerData[date] = 0;
            }
            if (typeof debt.subData[typeId].headerData[date] !== 'number') {
              debt.subData[typeId].headerData[date] = 0;
            }
            if (typeof netWorth.headerData[date] !== 'number') {
              netWorth.headerData[date] = 0;
            }

            debt.headerData[date] += account.data[date];
            debt.subData[typeId].headerData[date] += account.data[date];
            netWorth.headerData[date] += account.data[date];
          }
        });
        debt.subData[typeId].accounts.push(account);
      } else if (nodeAssetIds.some((id) => typeId.includes(id))) {
        if (typeId && displayLabel && !assets.subData[typeId]) {
          numberOfAssetTypes += 1;
          assets.subData[typeId] = {
            displayLabel,
            accounts: [],
            headerData: { ...zeroValueDates },
          };
        }
        dateList.forEach((date) => {
          if (typeof account.data[date] === 'number') {
            // We now know the acct isn't null, by default it is null, so we must change it to 0 so it can be summed
            if (typeof assets.headerData[date] !== 'number') {
              assets.headerData[date] = 0;
            }
            if (typeof assets.subData[typeId].headerData[date] !== 'number') {
              assets.subData[typeId].headerData[date] = 0;
            }
            if (typeof netWorth.headerData[date] !== 'number') {
              netWorth.headerData[date] = 0;
            }

            assets.headerData[date] += account.data[date];
            assets.subData[typeId].headerData[date] += account.data[date];
            netWorth.headerData[date] += account.data[date];
          }
        });
        assets.subData[typeId].accounts.push(account);
      }
    }
  });
  assets.subData = Object.entries(assets.subData).sort(([aId, _a], [bId, _b]) => {
    const matchingNodeIdForA = nodeAssetIds.find((nodeId) => aId.includes(nodeId));
    const matchingNodeIdForB = nodeAssetIds.find((nodeId) => bId.includes(nodeId));
    return nodeAssetIds.indexOf(matchingNodeIdForA) - nodeAssetIds.indexOf(matchingNodeIdForB);
  });

  debt.subData = Object.entries(debt.subData).sort(([aId, _a], [bId, _b]) => {
    const matchingNodeIdForA = nodeDebtIds.find((nodeId) => aId.includes(nodeId));
    const matchingNodeIdForB = nodeDebtIds.find((nodeId) => bId.includes(nodeId));
    return nodeDebtIds.indexOf(matchingNodeIdForA) - nodeDebtIds.indexOf(matchingNodeIdForB);
  });
  return {
    numberOfDebtTypes,
    numberOfAssetTypes,
    dateList,
    netWorth,
    assets,
    debt,
  };
};

export const getNetWorthReportForDisplay = createSelector(
  (state) => accountsSelectors.getLoadPending(state) || transactionsSelectors.getLoadPending(state),
  (state, props) => getAccountBalances(state, { ...props, accountsById: accountsSelectors.getAccountsById(state) }),
  (state) => accountsSelectors.getAccountsById(state),
  (_, props) => props.groupBy,
  (_, props) => props.timeStep,
  (_, props) => props.filterObject,
  (state) => accountsSelectors.getAccountsById(state),
  (loadPending, report, accountsById, groupBy, timeStep, filterObject, reportableAccounts) => {
    if (loadPending || !report) {
      return null;
    }

    const retData = { allTxns: null, chartData: {} };
    retData.allTxns = report.get('allTxns');
    retData.chartData.periods = report.get('periods').toArray();

    if (groupBy === 'account') {
      retData.chartData.orderedFields = getAccountsInPeriods(report.get('periodsByAcctId'), accountsById)
        .map((acc) => acc.name)
        .toList()
        .toArray();
    } else {
      retData.chartData.orderedFields = null;
    }
    retData.total = retData.chartData.periods[retData.chartData.periods.length - 1]?.total;
    retData.tableData = getNetWorthTableData(
      report.get('periodsByAcctId').toArray(),
      accountsById,
      timeStep,
      filterObject,
      reportableAccounts,
    );

    return ImmutableMap({ ...retData, rawReport: report });
  },
);

// wrapper to combine all above selectors into one call
export const getReportForDisplay = createSelector(
  (state) =>
    accountsSelectors.getLoadPending(state) ||
    categoriesSelectors.getLoadPending(state) ||
    transactionsSelectors.getLoadPending(state) ||
    tagsSelectors.getLoadPending(state),
  (state, props) => (props.pageInitialized && props.subSelector && props.subSelector(state, props)) || null,
  (loadPending, focusedReport) => {
    if (!loadPending && focusedReport) {
      return focusedReport;
    }
    return null;
  },
);

const getLastMonthInterval = () => constructPredefinedInterval({ value: 'LAST_MONTH' }, DateTime.local());
const getLast2MonthsInterval = () =>
  constructPredefinedInterval(
    {
      value: 'CUSTOM',
      startDate: getStartOfMonthFromThisMonth(DateTime.local(), -2),
      endDate: getStartOfMonthFromThisMonth(DateTime.local(), 0),
    },
    DateTime.local(),
  );
const NO_BILLS_OR_SUBSCRIPTIONS_FILTER = (txn) => !txn.isBill && !txn.isSubscription;

// returns goals by id with the extra value, contributedLastMonth
const getGoalsDataForLastMonth = createSelector(
  (state) => transactionsSelectors.getTransactionsByAccountId(state),
  (state) => goalsSelectors.getGoalsById(state),
  (txnsByAccountId, goalsById) => {
    const lastMonthInterval = getLastMonthInterval();

    const txnsByAccountIdFromLastMonthOnly = txnsByAccountId.map((txns) =>
      txns.filter((t) => t && intervalContainsInclusive(lastMonthInterval, DateTime.fromISO(t.postedOn))),
    );

    const incompleteGoalsStartedBeforeThisMonth = goalsById.filter(
      (goal) => !goal.get('completedOn') && DateTime.fromISO(goal.get('startOn')) < DateTime.local().startOf('month'),
    );

    return incompleteGoalsStartedBeforeThisMonth.map((goal) => {
      const txns = txnsByAccountIdFromLastMonthOnly.get(goal.accountId);
      const contributionsLastMonth = txns?.reduce((accumulator, val) => accumulator + val.amount, 0) || 0;
      return {
        ...goal.toJS(),
        // txns,
        contributionsLastMonth,
        percentFinished: Math.min(1, contributionsLastMonth / goal.get('contributionAmount')),
      };
    });
  },
);

export const getMonthlySummary = createSelector(
  (state) =>
    accountsSelectors.getLoadPending(state) ||
    categoriesSelectors.getLoadPending(state) ||
    transactionsSelectors.getLoadPending(state) ||
    goalsSelectors.getGoalsIsLoading(state) ||
    tagsSelectors.getLoadPending(state),
  categoriesSelectors.getCategoriesById,
  tagsSelectors.getTags,
  (state, props) =>
    getReportFilteredTxns(state, {
      ...props,
      dateRange: getLast2MonthsInterval(),
      reportSpecificFilter: getReportSpecificFilterFromReportName('spending'),
    }),
  (state, props) =>
    getReportFilteredTxns(state, {
      ...props,
      dateRange: getLast2MonthsInterval(),
      reportSpecificFilter: getReportSpecificFilterFromReportName('income'),
    }),
  (state) => getGoalsDataForLastMonth(state),
  (loadPending, categoriesById, tagsById, spendingTxns, incomeTxns, lastMonthGoalsData) => {
    if (loadPending || !spendingTxns || !incomeTxns) return null;

    const LAST_2_MONTHS_INTERVAL = getLast2MonthsInterval();

    const spendingNoBillsFilter = (txn) =>
      getReportSpecificFilterFromReportName('spending')(txn, categoriesById) &&
      NO_BILLS_OR_SUBSCRIPTIONS_FILTER(txn, categoriesById);

    const spendingByNone = getOverTimeData(
      spendingTxns,
      LAST_2_MONTHS_INTERVAL,
      'months',
      'none',
      categoriesById,
      tagsById,
      getReportSpecificFilterFromReportName('spending'),
    );
    const spendingByCategory = getOverTimeData(
      spendingTxns,
      LAST_2_MONTHS_INTERVAL,
      'months',
      'category',
      categoriesById,
      tagsById,
      spendingNoBillsFilter,
    );
    const spendingByPayee = getOverTimeData(
      spendingTxns,
      LAST_2_MONTHS_INTERVAL,
      'months',
      'payee',
      categoriesById,
      tagsById,
      spendingNoBillsFilter,
    );

    const incomeByNone = getOverTimeData(
      incomeTxns,
      LAST_2_MONTHS_INTERVAL,
      'months',
      'none',
      categoriesById,
      tagsById,
      getReportSpecificFilterFromReportName('income'),
    );

    const data = {};

    data.lastMonthIncomeTotal = incomeByNone.get('periods').get(1).total;
    data.monthBeforeLastIncomeTotal = incomeByNone.get('periods').get(0).total;
    data.lastMonthSpendingTotal = spendingByNone.get('periods').get(1).total;
    data.monthBeforeLastSpendingTotal = spendingByNone.get('periods').get(0).total;

    data.lastMonthExpensesOnBills =
      spendingByNone
        .get('periods')
        .get(1)
        .txns?.reduce(
          (accumulator, val) => (val.isBill || val.isSubscription ? accumulator + val.amount : accumulator),
          0,
        ) || 0;
    data.monthBeforeLastExpensesOnBills =
      spendingByNone
        .get('periods')
        .get(0)
        .txns?.reduce(
          (accumulator, val) => (val.isBill || val.isSubscription ? accumulator + val.amount : accumulator),
          0,
        ) || 0;

    let percentDirection = data.lastMonthIncomeTotal > data.monthBeforeLastIncomeTotal ? 1 : -1;
    data.percentChangeIncome =
      Math.sign(data.monthBeforeLastIncomeTotal) !== 0
        ? // division by 0 check
          (percentDirection * 100 * Math.abs(data.lastMonthIncomeTotal - data.monthBeforeLastIncomeTotal)) /
          Math.abs(data.monthBeforeLastIncomeTotal)
        : 100;
    percentDirection = data.lastMonthSpendingTotal < data.monthBeforeLastSpendingTotal ? 1 : -1; // more negative means more spending
    data.percentChangeSpending =
      Math.sign(data.monthBeforeLastSpendingTotal) !== 0
        ? // division by 0 check
          (percentDirection * 100 * Math.abs(data.lastMonthSpendingTotal - data.monthBeforeLastSpendingTotal)) /
          Math.abs(data.monthBeforeLastSpendingTotal) // -1 to flip spending amounts to calculate change
        : 100;

    data.orderedSpendingByCategory = ImmutableList(
      Object.entries(spendingByCategory.get('periods').get(1).groups)
        .map(([cat, val]) => {
          const lastMonthTotal = val.total || 0;
          const monthBeforeTotal =
            Object.entries(spendingByCategory.get('periods').get(0).groups).find(
              ([itemName, _]) => itemName === cat,
            )?.[1].total || 0;
          const percentDirec = lastMonthTotal < monthBeforeTotal ? 1 : -1; // more negative means more spending
          const percentChange =
            monthBeforeTotal !== 0
              ? (Math.abs(lastMonthTotal - monthBeforeTotal) / Math.abs(monthBeforeTotal)) * 100 * percentDirec
              : 100;
          return {
            coaName: cat,
            lastMonthTotal,
            monthBeforeTotal,
            percentChange,
          };
        })
        .filter((item) => item.lastMonthTotal !== 0)
        .sort((first, second) => first.lastMonthTotal - second.lastMonthTotal),
    );

    data.orderedSpendingByPayee = Object.entries(spendingByPayee.get('periods').get(1).groups)
      .map(([payee, val]) => {
        const lastMonthTotal = val.total || 0;
        const monthBeforeTotal =
          Object.entries(spendingByPayee.get('periods').get(0).groups).find(([itemName, _]) => itemName === payee)?.[1]
            .total || 0;
        const percentDirec = lastMonthTotal < monthBeforeTotal ? 1 : -1; // more negative means more spending
        const percentChange =
          monthBeforeTotal !== 0
            ? (Math.abs(lastMonthTotal - monthBeforeTotal) / Math.abs(monthBeforeTotal)) * 100 * percentDirec
            : 100;
        return {
          payeeName: payee,
          lastMonthTotal,
          monthBeforeTotal,
          percentChange,
          numTxns: val?.txns?.length || 0,
        };
      })
      .filter((item) => item.lastMonthTotal !== 0)
      .sort((first, second) => first.lastMonthTotal - second.lastMonthTotal);

    data.lastMonthGoalsData = lastMonthGoalsData;

    data.lastMonthNetIncomeTotal = data.lastMonthIncomeTotal + data.lastMonthSpendingTotal;
    const monthBeforeLastNetIncomeTotal = data.monthBeforeLastIncomeTotal + data.monthBeforeLastSpendingTotal;
    percentDirection = data.lastMonthNetIncomeTotal > monthBeforeLastNetIncomeTotal ? 1 : -1;
    data.percentChangeNetIncome =
      Math.round(percentDirection * 100 * Math.abs(data.lastMonthNetIncomeTotal - monthBeforeLastNetIncomeTotal)) /
      Math.abs(monthBeforeLastNetIncomeTotal);
    if (!Number.isFinite(data.percentChangeNetIncome)) {
      data.percentChangeNetIncome = data.percentChangeNetIncome > 0 ? 100 : -100;
    }

    return ImmutableMap(data);
  },
);
